/* globals mixpanel */

import {Component} from 'panel';
import moment from 'moment';

import {registerMPElement} from 'common/util/register-element';

import {Alert} from '../models.js';
import {CHANGE_TYPE_REMOVED, CHANGE_TYPE_SENT_FEEDBACK} from '../constants';
import template from './index.jade';
import css from './index.styl?inline';

import {getSentFeedbackAtMs, setSentFeedbackAtMs} from '../utils/common';
import {buildGlobalChangedAlert, buildGlobalChangeHandler} from '../utils/globalEvents.js';
import {fetchAlert} from '../utils/api';
import {generateMessage} from '../utils/message';
import {trackAlertEvent} from '../utils/tracking';

export default class SmartHubNotification extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      alert: `json`,
      'display-context': `string`,
      'explains-value': `json`,
      'last-item': `boolean`,
      'project-id': `string`,
      'utc-offset': `number`,
      visible: `string`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        alert: null,
        markReadWhenClosed: false,
        projectId: null,
        stopFeedbackAnimation: false,
      },
      helpers: {
        handleRemoveAlert: (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.fetch(`DELETE`);
          trackAlertEvent(`Smart Hub: Deleted Alert`, this.getAlert(), this.context);
          this.dispatchEvent(buildGlobalChangedAlert(CHANGE_TYPE_REMOVED, this.alert));
        },
        handleSentFeedback: (event) => {
          event.preventDefault();
          const {isUseful, sentFeedbackAtMs} = event.detail;

          const params = {
            is_useful: isUseful,
            display_context: this.attr(`display-context`),
          };
          this.fetch(`PATCH`, params);

          trackAlertEvent(`Smart Hub: Sent Feedback`, this.getAlert(), this.context, {is_useful: isUseful});

          this.setSentFeedbackAtMs(sentFeedbackAtMs);

          this.dispatchEvent(buildGlobalChangedAlert(CHANGE_TYPE_SENT_FEEDBACK, this.alert, {isUseful}));
        },
        isFeedbackDisabled: () => {
          return !!getSentFeedbackAtMs(this.state.alert);
        },
        showFeedback: () => {
          return this.getAlert().isAlertOwner && !this.state.stopFeedbackAnimation;
        },
        viewFullAlert: (event) => {
          event.preventDefault();
          event.stopPropagation();

          const modifierKey = event.ctrlKey || event.metaKey;

          Promise.all([
            this.incrementPeopleProperty(`Smart Hub: Total Alerts Clicked`),
            trackAlertEvent(`Smart Hub: Clicked Alert Link`, this.getAlert(), this.context),
          ]).then(this.navigateToLink(modifierKey));

          // Navigate to link if the mixpanel track calls doesn't finish within 300ms.
          setTimeout(this.navigateToLink(modifierKey), 300);

          this.dispatchEvent(
            new CustomEvent(`clickedLink`, {
              bubbles: true,
              composed: true,
              detail: {modifierKey},
            }),
          );
        },
      },
    };
  }

  // Only used in tests to inject spy dependency
  set appApi(value) {
    this._appApi = value;
  }

  get appApi() {
    return this._appApi;
  }

  get projectId() {
    return this.state.projectId;
  }

  get alert() {
    return this.state.alert;
  }

  // In what context is this alert being viewed?
  get context() {
    return {
      displayContext: this.attr(`display-context`),
    };
  }

  get explainsMessage() {
    return generateMessage(this.alert, {
      displayContext: this.attr(`display-context`),
      subContext: `EXPLAINS`,
      utcOffset: parseInt(this.attr(`utc-offset`)),
    });
  }

  get message() {
    return generateMessage(this.alert, {
      displayContext: this.attr(`display-context`),
      subContext: `SUMMARY`,
      typed: true,
      utcOffset: parseInt(this.attr(`utc-offset`)),
    });
  }

  get relativeTimestamp() {
    return moment(Number(this.getAlert().timestamp)).fromNow();
  }

  get selectedExplains() {
    return this.attr(`explains-value`);
  }

  connectedCallback() {
    this.updateAlertState();

    super.connectedCallback(...arguments);

    this._handleGlobalChangedAlert = buildGlobalChangeHandler(this, this.alert);
    document.addEventListener(`globalChangedAlert`, this._handleGlobalChangedAlert);
  }

  disconnectedCallback() {
    super.disconnectedCallback(...arguments);
    document.removeEventListener(`globalChangedAlert`, this._handleGlobalChangedAlert);
  }

  updateAlertState() {
    this.update({
      alert: this.attr(`alert`),
      projectId: this.attr(`project-id`),
    });
  }

  getAlert() {
    return Alert.create(this.alert, this.projectId, this.attr(`workspace-id`));
  }

  handleVisibilityChange() {
    const nextState = {};
    if (getSentFeedbackAtMs(this.alert)) {
      // Unmount the feedback component if we've been opened or closed during animation
      nextState.stopFeedbackAnimation = true;
    }

    this.update(nextState);
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);

    if (attr === `alert`) {
      this.updateAlertState();
    } else if (attr === `visible`) {
      this.handleVisibilityChange();
    }
  }

  fetch(method, params) {
    return fetchAlert(this.alert, this.projectId, method, params, this.appApi);
  }

  incrementPeopleProperty(propertyName) {
    return new Promise((resolve) => {
      mixpanel.people.increment(propertyName, 1, resolve);
    });
  }

  setSentFeedbackAtMs(ms) {
    const {alert} = this;
    setSentFeedbackAtMs(alert, ms);
    this.update({alert});
  }

  navigateToLink(modifierKey) {
    const href = this.getAlert().generatePermalink();
    if (!modifierKey) {
      window.location.href = href;
    } else {
      window.open(href);
    }
  }
}

registerMPElement(`mp-smart-hub-notification`, SmartHubNotification);
